import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const BulkMappingTools = ({
  onBulkMap,
  onClose,
  availableMedia = [],
  courseStructure = []
}) => {
  const [mappingMode, setMappingMode] = useState('pattern'); // 'pattern', 'type', 'tags', 'manual'
  const [patternRules, setPatternRules] = useState([
    { id: 1, pattern: '', targetType: 'video', enabled: true }
  ]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [autoSuggestions, setAutoSuggestions] = useState([]);

  // Generate auto-suggestions based on content analysis
  const generateSuggestions = () => {
    const suggestions = [];
    
    // Pattern-based suggestions
    const patterns = ['intro', 'javascript', 'react', 'css', 'html'];
    patterns?.forEach(pattern => {
      const matchingMedia = availableMedia?.filter(item => 
        item?.title?.toLowerCase()?.includes(pattern) ||
        item?.tags?.some(tag => tag?.toLowerCase()?.includes(pattern))
      );
      
      const matchingLessons = courseStructure
        ?.flatMap(course => course?.modules)
        ?.flatMap(module => module?.lessons)
        ?.filter(lesson => 
          lesson?.title?.toLowerCase()?.includes(pattern) && 
          lesson?.mediaRequired && 
          !lesson?.currentMedia
        );
      
      if (matchingMedia?.length > 0 && matchingLessons?.length > 0) {
        suggestions?.push({
          id: suggestions?.length + 1,
          type: 'pattern',
          pattern,
          mediaCount: matchingMedia?.length,
          targetCount: matchingLessons?.length,
          confidence: 0.8 + (Math.min(matchingMedia?.length, matchingLessons?.length) * 0.02)
        });
      }
    });
    
    // Type-based suggestions
    const typeMatches = {
      video: courseStructure
        ?.flatMap(course => course?.modules)
        ?.flatMap(module => module?.lessons)
        ?.filter(lesson => lesson?.mediaType === 'video' && lesson?.mediaRequired && !lesson?.currentMedia),
      image: courseStructure
        ?.flatMap(course => course?.modules)
        ?.flatMap(module => module?.lessons)
        ?.filter(lesson => lesson?.mediaType === 'image' && lesson?.mediaRequired && !lesson?.currentMedia)
    };
    
    Object.entries(typeMatches)?.forEach(([type, lessons]) => {
      const mediaOfType = availableMedia?.filter(item => item?.type === type && !item?.mapped);
      if (mediaOfType?.length > 0 && lessons?.length > 0) {
        suggestions?.push({
          id: suggestions?.length + 1,
          type: 'mediaType',
          mediaType: type,
          mediaCount: mediaOfType?.length,
          targetCount: lessons?.length,
          confidence: 0.9
        });
      }
    });
    
    setAutoSuggestions(suggestions?.sort((a, b) => b?.confidence - a?.confidence));
  };

  React.useEffect(() => {
    generateSuggestions();
  }, [availableMedia, courseStructure]);

  const mappingModeOptions = [
    { value: 'pattern', label: 'Por Padrão de Nome' },
    { value: 'type', label: 'Por Tipo de Mídia' },
    { value: 'tags', label: 'Por Tags' },
    { value: 'manual', label: 'Seleção Manual' }
  ];

  const addPatternRule = () => {
    setPatternRules(prev => [...prev, {
      id: Date.now(),
      pattern: '',
      targetType: 'video',
      enabled: true
    }]);
  };

  const updatePatternRule = (id, field, value) => {
    setPatternRules(prev => prev?.map(rule =>
      rule?.id === id ? { ...rule, [field]: value } : rule
    ));
  };

  const removePatternRule = (id) => {
    setPatternRules(prev => prev?.filter(rule => rule?.id !== id));
  };

  const handleBulkMap = () => {
    let mappingRules = [];
    
    switch (mappingMode) {
      case 'pattern':
        mappingRules = patternRules?.filter(rule => rule?.enabled && rule?.pattern);
        break;
      case 'type':
        mappingRules = [{ type: 'mediaType', mediaType: 'video' }];
        break;
      case 'tags':
        mappingRules = [{ type: 'tags', tags: ['tutorial', 'lesson'] }];
        break;
      case 'manual':
        mappingRules = selectedMedia?.map(mediaId => ({
          type: 'manual',
          mediaId,
          targetIds: selectedTargets
        }));
        break;
    }
    
    onBulkMap?.(mappingRules);
    onClose?.();
  };

  const applySuggestion = (suggestion) => {
    if (suggestion?.type === 'pattern') {
      setMappingMode('pattern');
      setPatternRules([{
        id: Date.now(),
        pattern: suggestion?.pattern,
        targetType: 'video',
        enabled: true
      }]);
    } else if (suggestion?.type === 'mediaType') {
      setMappingMode('type');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-tier-2 mb-6">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Icon name="Layers" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Ferramentas de Mapeamento em Lote</h3>
              <p className="text-sm text-muted-foreground font-caption">
                Configure regras para mapear múltiplos itens automaticamente
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Auto Suggestions */}
        {autoSuggestions?.length > 0 && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
              <span>Sugestões Automáticas</span>
            </h4>
            <div className="space-y-2">
              {autoSuggestions?.slice(0, 3)?.map((suggestion) => (
                <div key={suggestion?.id} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {suggestion?.type === 'pattern' ? `Padrão: "${suggestion?.pattern}"` : 
                         suggestion?.type === 'mediaType' ? `Tipo: ${suggestion?.mediaType}` : 
                         'Sugestão personalizada'}
                      </span>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        suggestion?.confidence >= 0.9 ? 'bg-success text-success-foreground' :
                        suggestion?.confidence >= 0.8 ? 'bg-warning text-warning-foreground': 'bg-muted text-muted-foreground'
                      }`}>
                        {Math.round(suggestion?.confidence * 100)}% confiança
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {suggestion?.mediaCount} mídia(s) → {suggestion?.targetCount} destino(s)
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applySuggestion(suggestion)}
                  >
                    Aplicar
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mapping Mode */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Modo de Mapeamento
          </label>
          <Select
            value={mappingMode}
            onValueChange={setMappingMode}
            options={mappingModeOptions}
            className="w-full"
          />
        </div>

        {/* Pattern-based Mapping */}
        {mappingMode === 'pattern' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Regras de Padrão</h4>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                onClick={addPatternRule}
              >
                Nova Regra
              </Button>
            </div>
            
            {patternRules?.map((rule, index) => (
              <div key={rule?.id} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                <Checkbox
                  checked={rule?.enabled}
                  onChange={(e) => updatePatternRule(rule?.id, 'enabled', e?.target?.checked)}
                />
                
                <div className="flex-1">
                  <Input
                    placeholder="Padrão de busca (ex: 'javascript', 'intro')"
                    value={rule?.pattern}
                    onChange={(e) => updatePatternRule(rule?.id, 'pattern', e?.target?.value)}
                    className="mb-2"
                  />
                  <Select
                    value={rule?.targetType}
                    onValueChange={(value) => updatePatternRule(rule?.id, 'targetType', value)}
                    options={[
                      { value: 'video', label: 'Apenas Vídeos' },
                      { value: 'image', label: 'Apenas Imagens' },
                      { value: 'any', label: 'Qualquer Tipo' }
                    ]}
                    className="w-48"
                  />
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => removePatternRule(rule?.id)}
                  className="text-error hover:text-error"
                />
              </div>
            ))}
          </div>
        )}

        {/* Type-based Mapping */}
        {mappingMode === 'type' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-medium text-foreground mb-3">Mapear Vídeos</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div>Vídeos disponíveis: {availableMedia?.filter(item => item?.type === 'video' && !item?.mapped)?.length}</div>
                <div>Lições precisando de vídeo: {courseStructure?.flatMap(c => c?.modules)?.flatMap(m => m?.lessons)?.filter(l => l?.mediaType === 'video' && l?.mediaRequired && !l?.currentMedia)?.length}</div>
              </div>
            </div>
            
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-medium text-foreground mb-3">Mapear Imagens</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div>Imagens disponíveis: {availableMedia?.filter(item => item?.type === 'image' && !item?.mapped)?.length}</div>
                <div>Lições precisando de imagem: {courseStructure?.flatMap(c => c?.modules)?.flatMap(m => m?.lessons)?.filter(l => l?.mediaType === 'image' && l?.mediaRequired && !l?.currentMedia)?.length}</div>
              </div>
            </div>
          </div>
        )}

        {/* Manual Selection */}
        {mappingMode === 'manual' && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-foreground mb-3">Selecionar Mídia</h4>
              <div className="max-h-48 overflow-y-auto space-y-2 border border-border rounded-lg p-3">
                {availableMedia?.map((item) => (
                  <div key={item?.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedMedia?.includes(item?.id)}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setSelectedMedia(prev => [...prev, item?.id]);
                        } else {
                          setSelectedMedia(prev => prev?.filter(id => id !== item?.id));
                        }
                      }}
                    />
                    <span className="text-sm text-foreground">{item?.title}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-foreground mb-3">Selecionar Destinos</h4>
              <div className="max-h-48 overflow-y-auto space-y-2 border border-border rounded-lg p-3">
                {courseStructure?.flatMap(course => 
                  course?.modules?.flatMap(module => 
                    module?.lessons?.filter(lesson => lesson?.mediaRequired)
                      ?.map(lesson => ({ ...lesson, moduleName: module?.title, courseName: course?.title }))
                  )
                )?.map((lesson) => (
                  <div key={lesson?.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedTargets?.includes(lesson?.id)}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setSelectedTargets(prev => [...prev, lesson?.id]);
                        } else {
                          setSelectedTargets(prev => prev?.filter(id => id !== lesson?.id));
                        }
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground font-medium">{lesson?.title}</div>
                      <div className="text-xs text-muted-foreground">{lesson?.courseName} &gt; {lesson?.moduleName}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Preview Results */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3">Preview de Resultados</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary">
                {mappingMode === 'pattern' ? 
                  availableMedia?.filter(item => 
                    patternRules?.some(rule => 
                      rule?.enabled && rule?.pattern && 
                      item?.title?.toLowerCase()?.includes(rule?.pattern?.toLowerCase())
                    )
                  )?.length : 
                  mappingMode === 'manual' ? selectedMedia?.length : '~'}
              </div>
              <div className="text-xs text-muted-foreground">Mídia Selecionada</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-secondary">
                {mappingMode === 'pattern' ? 
                  courseStructure?.flatMap(c => c?.modules)?.flatMap(m => m?.lessons)
                    ?.filter(l => l?.mediaRequired && !l?.currentMedia)?.length :
                  mappingMode === 'manual' ? selectedTargets?.length : '~'}
              </div>
              <div className="text-xs text-muted-foreground">Destinos Disponíveis</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-success">
                {mappingMode === 'manual' ? Math.min(selectedMedia?.length, selectedTargets?.length) :'~'}
              </div>
              <div className="text-xs text-muted-foreground">Conexões Estimadas</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            iconName="Layers"
            onClick={handleBulkMap}
            disabled={
              (mappingMode === 'pattern' && !patternRules?.some(rule => rule?.enabled && rule?.pattern)) ||
              (mappingMode === 'manual' && (selectedMedia?.length === 0 || selectedTargets?.length === 0))
            }
          >
            Aplicar Mapeamento
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkMappingTools;